import React, { useEffect, useState } from 'react';
import { GraduationCap, Library, HeartPulse, Sparkles, RefreshCw, Zap, ScrollText, Activity, Network, TrendingUp, DollarSign, Edit2, Check } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import EmpathyTrendChart from '@/components/EmpathyTrendChart';

/**
 * AI Mentors — Shu'aib, Idris & Wujdān dashboard.
 *
 * Shows the running curriculum age of all 16 AIs, the lessons logged so far,
 * the Wujdān emotional climate, and admin controls to:
 *   • Train Now             — Shu'aib: 1 LLM call per AI × 16 = 1 cycle (+10y each)
 *   • Speed Train           — Shu'aib: up to 10 cycles back-to-back
 *   • Evaluate Emotion Now  — Wujdān: 1 LLM call per AI with recent activity
 *   • Cross-Pollinate Now   — Wujdān: 1 LLM call (top → bottom voice transfer)
 */
export default function AIMentors() {
  const [data, setData] = useState(null);
  const [emo, setEmo] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b, t] = await Promise.all([
        api.get('/ai/mentors/status'),
        api.get('/ai/mentors/emotional-status').catch(() => ({ data: { indexes: [], total_critiques_logged: 0, last_cross_pollination: null } })),
        api.get(`/ai/mentors/empathy-trend?days=${trendDays}`).catch(() => ({ data: null })),
      ]);
      setData(a.data);
      setEmo(b.data);
      setTrend(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load mentorship status.');
    } finally { setLoading(false); }
  };

  /* eslint-disable -- v6 react-hooks/set-state-in-effect + exhaustive-deps false-positives on plain async polling */
  useEffect(() => { load(); }, [trendDays]);
  /* eslint-enable */

  const trainNow = async () => {
    setBusy(true);
    toast.info("Shu'aib is composing lessons for 16 AIs — ~8 seconds in parallel.");
    try {
      const { data } = await api.post('/ai/mentors/train-now');
      if (data?.skipped) toast.error(`Training skipped: ${data.skipped}`);
      else toast.success(`One cycle complete · ${Object.keys(data.personas || {}).length} AIs advanced +10y.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Training failed.');
    } finally { setBusy(false); }
  };

  const speedTrain = async () => {
    const cycles = parseInt(window.prompt('Cycles? (1-10) Each cycle = +10y per AI = 16 LLM calls.', '3') || '0', 10);
    if (!cycles || cycles < 1) return;
    setBusy(true);
    toast.info(`Speed-training ${cycles} cycle(s) — please wait.`);
    try {
      await api.post('/ai/mentors/speed-train', { cycles });
      toast.success(`Speed-train complete · advanced +${cycles * 10}y per AI.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speed-train failed.');
    } finally { setBusy(false); }
  };

  const evaluateEmotion = async () => {
    setBusy(true);
    toast.info("Wujdān is reading the team's recent voice — skips silent personas.");
    try {
      const { data } = await api.post('/ai/mentors/emo-evaluate-now');
      if (data?.skipped) toast.error(`Skipped: ${data.skipped}`);
      else if ((data?.evaluated ?? 0) === 0) toast.info(data?.note || 'No recent chats to evaluate.');
      else toast.success(`Wujdān evaluated ${data.evaluated} persona${data.evaluated === 1 ? '' : 's'}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Emotional evaluation failed.');
    } finally { setBusy(false); }
  };

  const crossPollinate = async () => {
    setBusy(true);
    toast.info("Distilling top-empathy voice into bottom-empathy AI — one LLM call.");
    try {
      const { data } = await api.post('/ai/mentors/emo-cross-pollinate');
      if (data?.skipped) toast.info(`Skipped: ${data.skipped}`);
      else toast.success(`Cross-pollinated · ${data.from} → ${data.to}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cross-pollination failed.');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="ve-section text-sm text-slate-500">Loading the curriculum…</div>;

  const personas = data?.personas || [];
  const mentors = personas.filter(p => ['shuaib', 'idris', 'wujdan'].includes(p.persona));
  const others  = personas.filter(p => !['shuaib', 'idris', 'wujdan'].includes(p.persona));
  // Lookup of emotional index by persona for tiles
  const emoIdx = (emo?.indexes || []).reduce((m, i) => ({ ...m, [i.persona]: i }), {});

  return (
    <div className="ve-section" data-testid="ai-mentors-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="pal-bracket flex items-center gap-2">
            <GraduationCap size={14} className="text-[#8B3FC5]"/>
            AI Mentors
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Shu&apos;aib · Idris · Wujdān. <span className="ve-gold-text">Growing the team — mind, knowledge, heart.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Three mentor AIs train the other 13 advisors every day. <b>Shu&apos;aib</b> (شُعَيب) sharpens
            their craft · <b>Idris</b> (إِدْرِيس) feeds them strategy (Qatar NDS3 2024-2030) ·
            <b> Wujdān</b> (وِجْدَان) — inner conscience — gently corrects their empathy. No vetoes —
            they learn slowly through mistakes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            data-testid="mentors-train-now"
            disabled={busy}
            onClick={trainNow}
            className="ve-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            title="Shu'aib: 1 cycle, +10y each AI"
          >
            <RefreshCw size={14}/> Train (Shu&apos;aib)
          </button>
          <button
            data-testid="mentors-speed-train"
            disabled={busy}
            onClick={speedTrain}
            className="ve-btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
            title="Up to 10 back-to-back cycles"
          >
            <Zap size={14}/> Speed Train
          </button>
          <button
            data-testid="wujdan-evaluate-now"
            disabled={busy}
            onClick={evaluateEmotion}
            className="ve-btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
            title="Wujdān: evaluate the team's recent emotional tone"
          >
            <Activity size={14}/> Evaluate Emotion
          </button>
          <button
            data-testid="wujdan-cross-pollinate"
            disabled={busy}
            onClick={crossPollinate}
            className="ve-btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
            title="Top empathy AI teaches bottom empathy AI"
          >
            <Network size={14}/> Cross-Pollinate
          </button>
        </div>
      </div>

      {/* Mentors strip */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {mentors.map((m) => (
          <MentorCard key={m.persona} c={m} />
        ))}
      </div>

      {/* Emotional climate panel */}
      <div className="mt-8">
        <div className="pal-bracket text-[#5E1F8B] flex items-center gap-2">
          <HeartPulse size={14}/> Emotional Climate (Wujdān)
        </div>
        <p className="text-xs text-slate-500 dark:text-white/60 mt-1">
          {emo?.total_critiques_logged
            ? `${emo.total_critiques_logged} critique${emo.total_critiques_logged === 1 ? '' : 's'} logged.`
            : `No emotional critiques yet — click "Evaluate Emotion" to seed the index, or wait for the 05:00 UTC cycle.`}
          {emo?.last_cross_pollination && (
            <span className="ml-2">
              · Last cross-pollination: <b>{emo.last_cross_pollination.cross_pollinated_from}</b> → <b>{emo.last_cross_pollination.persona}</b>
            </span>
          )}
        </p>
        {emo?.indexes?.length > 0 && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {emo.indexes.map((i) => (
              <EmotionalIndexTile key={i.persona} idx={i}/>
            ))}
          </div>
        )}

        {/* Empathy trend over time */}
        <div className="mt-5 pal-card pal-grid-bg p-5 lg:p-6" data-testid="empathy-trend-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="pal-bracket text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
              <TrendingUp size={13} /> Empathy trend
            </div>
            <div className="flex items-center gap-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setTrendDays(d)}
                  data-testid={`empathy-range-${d}`}
                  className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded transition ${
                    trendDays === d
                      ? 'bg-[#8B3FC5] text-white'
                      : 'border border-slate-200 dark:border-white/10 text-slate-500 dark:text-white/55 hover:border-[#8B3FC5]/60'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-white/55">
            Daily mean of Wujdān&apos;s empathy_score per AI. Click any persona in the legend to isolate its line.
          </p>
          <div className="mt-4">
            <EmpathyTrendChart data={trend} />
          </div>
        </div>
      </div>

      {/* Strategic context cards */}
      <div className="mt-8">
        <div className="pal-bracket text-[#5E1F8B] flex items-center gap-2">
          <ScrollText size={14}/> Strategic Library (Idris)
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-white/55">
          Every day at 04:30 UTC Idris researches <b className="text-slate-700 dark:text-white">{data?.library?.books_per_persona_per_day ?? 4} best new books / research papers / publications per AI</b> across all 26 sectors,
          then drafts a one-line teaching plan for each AI so they grow with the readings.
          {' '}<a href="/dashboard/library" data-testid="library-open-fullpage" className="text-[#5E1F8B] hover:underline font-bold">Open full library →</a>
        </p>

        {/* Counters strip */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="idris-library-counters">
          <div className="pal-card pal-grid-bg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Library size</div>
            <div className="display text-3xl mt-1 ve-gold-text" data-testid="library-total">{(data?.library?.total ?? 0).toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 mt-1">books · papers · reports</div>
          </div>
          <div className="pal-card pal-grid-bg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Added today</div>
            <div className="display text-3xl mt-1" data-testid="library-today">+{(data?.library?.today_count ?? 0).toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 mt-1">across all sectors</div>
          </div>
          <div className="pal-card pal-grid-bg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Reading-plan lessons</div>
            <div className="display text-3xl mt-1" data-testid="library-lessons">{(data?.library?.todays_lessons || []).length}</div>
            <div className="text-[10px] text-slate-500 mt-1">injected into AI prompts today</div>
          </div>
          <BudgetTile budget={data?.library?.budget} onSaved={load} />
        </div>

        {/* Ingest CTA */}
        <div className="mt-3">
          <button
            type="button"
            data-testid="library-ingest-now"
            onClick={async () => {
              setBusy(true);
              toast.info("Idris is fetching free Open Library + arXiv candidates → tiny cheap LLM picks the best…");
              try {
                const { data: r } = await api.post('/ai/mentors/library/ingest-now', {}, { timeout: 240000 });
                if (r?.skipped) toast.info(`Library skipped: ${r.reason} (today=${r.today_count})`);
                else toast.success(`Idris added ${r.added} books & ${r.lessons_added} reading-plan lessons across ${r.sectors_processed} sectors.`);
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Ingest failed.");
              } finally { setBusy(false); }
            }}
            disabled={busy}
            className="ve-btn-secondary text-xs disabled:opacity-50"
          >
            <Zap size={12}/> Run today&apos;s ingest
          </button>
        </div>

        {/* Per-persona reading list count */}
        {Object.keys(data?.library?.by_persona || {}).length > 0 && (
          <div className="mt-4 pal-card pal-grid-bg p-4" data-testid="idris-library-per-persona">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">Reading shelf · per AI</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {Object.entries(data.library.by_persona)
                .sort((a, b) => b[1] - a[1])
                .map(([persona, n]) => (
                <div
                  key={persona}
                  data-testid={`library-persona-${persona}`}
                  className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-[#FFF8E5]/60 dark:bg-[#3a2f0a]/40 border border-[#8B3FC5]/25"
                >
                  <span className="text-xs font-bold capitalize">{persona}</span>
                  <span className="mono text-xs text-[#5E1F8B] font-bold">{n}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Today's reading-plan lessons */}
        {(data?.library?.todays_lessons || []).length > 0 && (
          <div className="mt-4 pal-card pal-grid-bg p-4" data-testid="idris-library-todays-lessons">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
              Today&apos;s reading-plan lessons · {data.library.todays_lessons.length}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
              {data.library.todays_lessons.slice(0, 12).map((l, idx) => (
                <div key={idx} className="text-sm leading-snug" data-testid={`library-lesson-${l.persona}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#5E1F8B] font-bold">{l.persona}</div>
                  <div className="text-slate-800 dark:text-white/90 mt-0.5">{l.new_principle}</div>
                  {l.linked_book_titles?.length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-1 truncate">
                      From: {l.linked_book_titles.slice(0, 3).join(' · ')}
                      {l.linked_book_titles.length > 3 ? ` +${l.linked_book_titles.length - 3} more` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent books feed */}
        {(data?.library?.recent || []).length > 0 && (
          <div className="mt-4 pal-card pal-grid-bg p-4" data-testid="idris-library-recent">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">Recently added · last {data.library.recent.length}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
              {data.library.recent.map((b) => (
                <div key={b.book_id} className="text-sm leading-snug" data-testid={`library-book-${b.book_id}`}>
                  <div className="font-bold truncate" title={b.title}>{b.title}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {b.author}{b.year ? ` · ${b.year}` : ''}
                    {b.type && b.type !== 'book' ? <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] bg-[#8B3FC5]/15 text-[#5E1F8B] font-bold">{b.type}</span> : null}
                    {' · '}<span className="text-[#5E1F8B]">{b.target_persona || b.domain}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Doctrine docs (NDS3 etc.) */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.strategic_context_docs || []).map((d) => (
            <div key={d.doc_id} className="pal-card pal-grid-bg p-4" data-testid={`strategic-doc-${d.doc_id}`}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Doctrine doc</div>
              <div className="text-sm font-bold mt-1">{d.title}</div>
              <div className="text-xs text-slate-500 mt-1">{d.outcomes_count} outcomes indexed</div>
            </div>
          ))}
          {(data?.strategic_context_docs || []).length === 0 && (
            <div className="text-sm text-slate-500">No strategic docs yet — re-seed to ingest NDS3.</div>
          )}
        </div>
      </div>

      {/* All students */}
      <div className="mt-8">
        <div className="pal-bracket text-[#5E1F8B] flex items-center gap-2">
          <Sparkles size={14}/> Students · 13 AIs
        </div>
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {others.map((c) => (
            <PersonaCurriculumRow key={c.persona} c={c} emo={emoIdx[c.persona]}/>
          ))}
        </div>
      </div>

      {/* Footer stats */}
      <div className="mt-8 pal-card pal-grid-bg p-4 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
        <span>Total lessons (Shu&apos;aib): <b className="text-slate-700 dark:text-white">{data?.total_lessons_logged ?? 0}</b></span>
        <span>Total critiques (Wujdān): <b className="text-slate-700 dark:text-white">{emo?.total_critiques_logged ?? 0}</b></span>
        <span>Initial absorbed age: <b className="text-slate-700 dark:text-white">{data?.initial_age_years?.toLocaleString() || '—'} years</b></span>
        <span>Cycle frequency: <b className="text-slate-700 dark:text-white">Daily · 04:00 / 05:00 UTC · Fri 06:00 UTC</b></span>
      </div>
    </div>
  );
}

function MentorCard({ c }) {
  const meta = {
    shuaib: { Icon: GraduationCap, name: "Shu'aib", ar: 'شُعَيب' },
    idris:  { Icon: Library,       name: 'Idris',  ar: 'إِدْرِيس' },
    wujdan: { Icon: HeartPulse,    name: 'Wujdān', ar: 'وِجْدَان' },
  }[c.persona] || { Icon: Sparkles, name: c.persona, ar: '' };
  const Icon = meta.Icon;
  return (
    <div className="pal-card pal-grid-bg p-5 border-l-4 border-l-[#8B3FC5]" data-testid={`mentor-card-${c.persona}`}>
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
          <Icon size={22} className="text-[#8B3FC5]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="display text-xl font-bold">{meta.name}</div>
              <div className="text-[16px] text-[#8B3FC5] font-bold" dir="rtl">{meta.ar}</div>
            </div>
            <span
              data-testid={`mentor-age-${c.persona}`}
              className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]"
            >
              Age {Number(c.age_years).toLocaleString()}y
            </span>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{c.field}</div>
          <p className="mt-1 text-sm text-slate-700 dark:text-white/80 leading-snug">{c.current_focus}</p>
        </div>
      </div>
    </div>
  );
}

function EmotionalIndexTile({ idx }) {
  const score = Math.round(idx.empathy_index || 0);
  const band = score >= 75 ? 'high' : score >= 50 ? 'mid' : 'low';
  const bandColors = {
    high: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    mid:  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    low:  'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  }[band];
  return (
    <div className="pal-card pal-grid-bg p-3" data-testid={`emo-index-${idx.persona}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold capitalize truncate">{idx.persona}</div>
        <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] ${bandColors}`}>
          {score} empathy
        </span>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1 text-[10px]">
        <Mini label="Warm" v={idx.warmth}/>
        <Mini label="Valid" v={idx.validation}/>
        <Mini label="Robot" v={idx.robotic} invert/>
        <Mini label="Frust" v={idx.frustration_mirror} invert/>
      </div>
      {idx.recent_critiques?.[0]?.principle && (
        <div className="mt-2 text-[11px] text-slate-500 dark:text-white/60 leading-snug">
          <span className="text-[#5E1F8B] font-bold">›</span> {idx.recent_critiques[0].principle}
        </div>
      )}
    </div>
  );
}

function Mini({ label, v, invert }) {
  const val = Math.round(v || 0);
  const good = invert ? val <= 30 : val >= 60;
  const ok   = invert ? val <= 60 : val >= 30;
  const color = good ? 'text-emerald-600' : ok ? 'text-amber-600' : 'text-rose-600';
  return (
    <div className="flex flex-col items-center">
      <div className={`text-xs font-bold ${color}`}>{val}</div>
      <div className="text-[9px] uppercase tracking-[0.12em] text-slate-400">{label}</div>
    </div>
  );
}

function PersonaCurriculumRow({ c, emo }) {
  const empathy = emo ? Math.round(emo.empathy_index || 0) : null;
  return (
    <div className="pal-card pal-grid-bg p-4" data-testid={`curriculum-row-${c.persona}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-bold capitalize">{c.persona}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-0.5">{c.field}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            data-testid={`curriculum-age-${c.persona}`}
            className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]"
          >
            Age {Number(c.age_years).toLocaleString()}y · {c.cycles_completed || 0} cycles
          </span>
          {empathy != null && (
            <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/60">
              empathy {empathy}
            </span>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-600 dark:text-white/70 leading-snug line-clamp-2">{c.current_focus}</p>
      {(c.recent_lessons || []).length > 0 && (
        <div className="mt-2 space-y-1">
          {c.recent_lessons.slice(0, 2).map((lsn) => (
            <div key={lsn.lesson_id} className="text-[11px] text-slate-500 dark:text-white/60 leading-snug">
              <span className="text-[#5E1F8B] font-bold">›</span> {lsn.new_principle}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function BudgetTile({ budget, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [cap, setCap] = useState(budget?.cap_usd ?? 10);
  const [saving, setSaving] = useState(false);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive */
  useEffect(() => { setCap(budget?.cap_usd ?? 10); }, [budget?.cap_usd]);
  /* eslint-enable */

  const save = async () => {
    const n = Number(cap);
    if (!n || n < 1 || n > 10000) { toast.error('Cap must be between $1 and $10000.'); return; }
    setSaving(true);
    try {
      await api.put('/ai/mentors/budget', { cap_usd: n });
      toast.success(`Monthly LLM cap set to $${n.toFixed(2)}.`);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update cap.');
    } finally { setSaving(false); }
  };

  const spent = budget?.spent_usd ?? 0;
  const capUsd = budget?.cap_usd ?? 10;
  const pct = Math.min(100, budget?.percent ?? 0);
  const barColor =
    budget?.over_cap ? 'bg-rose-500'
    : pct >= 85 ? 'bg-amber-500'
    : 'bg-emerald-500';

  return (
    <div className="pal-card pal-grid-bg p-4" data-testid="library-budget-tile">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold flex items-center gap-1.5">
          <DollarSign size={11}/> Monthly LLM spend
        </div>
        {!editing ? (
          <button type="button" onClick={() => setEditing(true)} data-testid="library-budget-edit"
            className="text-[10px] text-slate-400 hover:text-[#5E1F8B] uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1">
            <Edit2 size={10}/> Cap
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500">$</span>
            <input
              type="number"
              min="1"
              step="1"
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              data-testid="library-budget-cap-input"
              className="w-16 text-xs px-1.5 py-1 rounded border border-slate-200 dark:border-white/20 text-right"
            />
            <button type="button" onClick={save} disabled={saving} data-testid="library-budget-save"
              className="text-[10px] uppercase tracking-[0.15em] text-emerald-600 font-bold disabled:opacity-50 inline-flex items-center gap-1">
              <Check size={11}/>
            </button>
          </div>
        )}
      </div>
      <div className="display text-2xl mt-1.5" data-testid="library-budget-spent">
        ${spent.toFixed(4)}
        <span className="text-sm font-normal text-slate-500"> / ${capUsd.toFixed(0)}</span>
      </div>
      <div className="mt-2 h-1.5 bg-slate-100 dark:bg-white/10 rounded overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} data-testid="library-budget-bar"/>
      </div>
      <div className="text-[10px] text-slate-500 mt-1.5">
        {budget?.over_cap
          ? 'Cap hit — daily ingest will use free Open Library + arXiv only.'
          : budget?.near_cap
            ? `${pct.toFixed(1)}% of cap — slowing down soon.`
            : `${(budget?.calls ?? 0)} LLM calls this month`}
      </div>
    </div>
  );
}
